import React, { useState, useMemo } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  Box,
  Stack,
  Typography,
  Tabs,
  Tab,
  Skeleton,
  Button,
  IconButton,
  Tooltip,
  Snackbar,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Switch,
  Chip,
  TextField,
  Collapse,
} from "@mui/material";
import {
  Visibility as PreviewIcon,
  Share as ShareIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Lock as LockIcon,
  ContentCopy as CopyIcon,
  Close as CloseIcon,
} from "@mui/icons-material";
import { tokens } from "@/tokens";
import { useAppSelector } from "@/store";
import { roles } from "@/roles";
import {
  useGetFirstSaleCaseQuery,
  useGetFirstSaleSupplierQuery,
  useGetFirstSaleDocumentsQuery,
  useGetFirstSaleValidationQuery,
  useGetFirstSaleActivityEventsQuery,
  useGetFirstSaleCaseQuestionsQuery,
  useGetFirstSaleGeneratedDocsQuery,
  useDeleteFirstSaleCaseMutation,
} from "@/services/first-sale";
import {
  useGenerateFirstSalePortalTokenMutation,
  useTogglePortal2faMutation,
  useAddPortalAuthorizedEmailMutation,
  useRemovePortalAuthorizedEmailMutation,
} from "@/services/first-sale-portal";
import FirstSaleBreadcrumb from "../components/first-sale-breadcrumb";
import { StatusBadge } from "../components/status-badge";
import { DocumentList } from "../components/document-list";
import ActivityTimeline from "../components/activity-timeline";
import SupplierQuestionnaireTab from "../components/supplier-questionnaire-tab";
import GeneratedDocsTab from "../components/generated-docs-tab";
import { EditCaseModal } from "./edit-case-modal";

const formatCurrency = (value: number | null | undefined) => {
  if (value == null) return "\u2014";
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
  }).format(value);
};

interface SharePortalDialogProps {
  open: boolean;
  onClose: () => void;
  caseId: string;
  portalUrl: string;
  portal2faEnabled: boolean;
  portalAuthorizedEmails: string[];
  tenantVars: { tenantId?: string };
}

const SharePortalDialog: React.FC<SharePortalDialogProps> = ({
  open,
  onClose,
  caseId,
  portalUrl,
  portal2faEnabled,
  portalAuthorizedEmails,
  tenantVars,
}) => {
  const [is2faEnabled, setIs2faEnabled] = useState(portal2faEnabled);
  const [emails, setEmails] = useState<string[]>(portalAuthorizedEmails);
  const [newEmail, setNewEmail] = useState("");
  const [linkCopied, setLinkCopied] = useState(false);

  const [togglePortal2fa, { isLoading: isToggling }] = useTogglePortal2faMutation();
  const [addPortalAuthorizedEmail, { isLoading: isAdding }] = useAddPortalAuthorizedEmailMutation();
  const [removePortalAuthorizedEmail] = useRemovePortalAuthorizedEmailMutation();

  // Sync props when dialog opens with fresh data
  React.useEffect(() => {
    if (open) {
      setIs2faEnabled(portal2faEnabled);
      setEmails(portalAuthorizedEmails);
      setNewEmail("");
      setLinkCopied(false);
    }
  }, [open, portal2faEnabled, portalAuthorizedEmails]);

  const handleCopyLink = async () => {
    await navigator.clipboard.writeText(portalUrl);
    setLinkCopied(true);
    setTimeout(() => setLinkCopied(false), 2000);
  };

  const handleToggle2fa = async (enabled: boolean) => {
    setIs2faEnabled(enabled);
    try {
      await togglePortal2fa({ caseId, ...tenantVars, enabled }).unwrap();
    } catch (err) {
      setIs2faEnabled(!enabled);
    }
  };

  const handleAddEmail = async () => {
    const trimmed = newEmail.trim().toLowerCase();
    if (!trimmed || emails.includes(trimmed)) return;
    try {
      const updated = await addPortalAuthorizedEmail({ caseId, ...tenantVars, email: trimmed }).unwrap();
      setEmails(updated);
      setNewEmail("");
    } catch (err) {
      // noop
    }
  };

  const handleRemoveEmail = async (email: string) => {
    try {
      const updated = await removePortalAuthorizedEmail({ caseId, ...tenantVars, email }).unwrap();
      setEmails(updated);
    } catch (err) {
      // noop
    }
  };

  const handleEmailKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter") {
      e.preventDefault();
      handleAddEmail();
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: tokens.radius.md,
          overflow: "hidden",
        },
      }}
    >
      {/* Header */}
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        sx={{ px: 2.5, py: 1.75, borderBottom: `1px solid ${tokens.border.light}` }}
      >
        <Stack direction="row" alignItems="center" spacing={1}>
          <ShareIcon sx={{ fontSize: 16, color: tokens.text.secondary }} />
          <Typography
            sx={{
              fontSize: tokens.fontSize.sm2,
              fontWeight: tokens.fontWeight.heavy,
              color: tokens.text.primary,
            }}
          >
            Share Portal
          </Typography>
        </Stack>
        <IconButton size="small" onClick={onClose} sx={{ color: tokens.text.muted }}>
          <CloseIcon sx={{ fontSize: 16 }} />
        </IconButton>
      </Stack>

      <Box sx={{ px: 2.5, py: 2.5 }}>
        {/* Portal Link */}
        <Typography
          sx={{
            fontSize: tokens.fontSize.xs,
            fontWeight: tokens.fontWeight.semibold,
            color: tokens.text.secondary,
            textTransform: "uppercase",
            letterSpacing: "0.05em",
            mb: 0.75,
          }}
        >
          Portal Link
        </Typography>
        <Stack
          direction="row"
          spacing={1}
          alignItems="center"
          sx={{
            p: 1,
            bgcolor: tokens.bg.muted,
            borderRadius: tokens.radius.sm,
            border: `1px solid ${tokens.border.default}`,
            mb: 2.5,
          }}
        >
          <Typography
            sx={{
              flex: 1,
              fontSize: tokens.fontSize.xs,
              fontFamily: "monospace",
              color: tokens.text.body,
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            {portalUrl}
          </Typography>
          <Button
            size="small"
            startIcon={<CopyIcon sx={{ fontSize: 12 }} />}
            onClick={handleCopyLink}
            sx={{
              textTransform: "none",
              fontSize: tokens.fontSize["2xs"],
              fontWeight: tokens.fontWeight.semibold,
              color: linkCopied ? tokens.status.success : tokens.brand.primary,
              borderRadius: tokens.radius.sm,
              px: 1.25,
              py: 0.25,
              minWidth: 70,
              whiteSpace: "nowrap",
              "&:hover": { bgcolor: `${tokens.brand.primary}08` },
            }}
          >
            {linkCopied ? "Copied!" : "Copy"}
          </Button>
        </Stack>

        {/* Divider */}
        <Box sx={{ borderTop: `1px solid ${tokens.border.light}`, mb: 2.5 }} />

        {/* 2FA Toggle */}
        <Stack direction="row" alignItems="flex-start" justifyContent="space-between">
          <Stack direction="row" alignItems="center" spacing={1}>
            <LockIcon sx={{ fontSize: 14, color: tokens.text.secondary }} />
            <Box>
              <Typography
                sx={{
                  fontSize: tokens.fontSize.sm2,
                  fontWeight: tokens.fontWeight.semibold,
                  color: tokens.text.primary,
                }}
              >
                Require Email Verification
              </Typography>
              <Typography sx={{ fontSize: tokens.fontSize.xs, color: tokens.text.muted, mt: 0.25 }}>
                Suppliers must verify their email before accessing the portal.
              </Typography>
            </Box>
          </Stack>
          <Switch
            checked={is2faEnabled}
            disabled={isToggling}
            onChange={(_, checked) => handleToggle2fa(checked)}
            size="small"
            sx={{
              flexShrink: 0,
              "& .MuiSwitch-switchBase.Mui-checked": {
                color: tokens.brand.primary,
              },
              "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": {
                backgroundColor: tokens.brand.primary,
              },
            }}
          />
        </Stack>

        {/* Authorized Emails — only shown when 2FA is on */}
        <Collapse in={is2faEnabled}>
          <Box sx={{ mt: 2, pl: 0.5 }}>
            <Typography
              sx={{
                fontSize: tokens.fontSize.xs,
                fontWeight: tokens.fontWeight.semibold,
                color: tokens.text.secondary,
                textTransform: "uppercase",
                letterSpacing: "0.05em",
                mb: 1,
              }}
            >
              Authorized Emails
            </Typography>

            {emails.length === 0 ? (
              <Typography sx={{ fontSize: tokens.fontSize.xs, color: tokens.text.muted, mb: 1.5 }}>
                No authorized emails yet. Add emails that will be allowed to access this portal.
              </Typography>
            ) : (
              <Stack direction="row" flexWrap="wrap" gap={0.75} sx={{ mb: 1.5 }}>
                {emails.map((email) => (
                  <Chip
                    key={email}
                    label={email}
                    size="small"
                    onDelete={() => handleRemoveEmail(email)}
                    sx={{
                      fontSize: tokens.fontSize.xs,
                      bgcolor: tokens.bg.muted,
                      color: tokens.text.body,
                      border: `1px solid ${tokens.border.default}`,
                      "& .MuiChip-deleteIcon": {
                        fontSize: 14,
                        color: tokens.text.secondary,
                        "&:hover": { color: tokens.status.danger },
                      },
                    }}
                  />
                ))}
              </Stack>
            )}

            {/* Add email form */}
            <Stack direction="row" spacing={1} alignItems="center">
              <TextField
                value={newEmail}
                onChange={(e) => setNewEmail(e.target.value)}
                onKeyDown={handleEmailKeyDown}
                placeholder="supplier@example.com"
                size="small"
                type="email"
                sx={{
                  flex: 1,
                  "& .MuiInputBase-input": {
                    fontSize: tokens.fontSize.xs,
                    py: 0.75,
                  },
                  "& .MuiOutlinedInput-notchedOutline": {
                    borderColor: tokens.border.default,
                  },
                }}
              />
              <Button
                size="small"
                variant="outlined"
                disabled={isAdding || !newEmail.trim()}
                onClick={handleAddEmail}
                sx={{
                  textTransform: "none",
                  fontSize: tokens.fontSize.xs,
                  fontWeight: tokens.fontWeight.semibold,
                  color: tokens.text.primary,
                  border: `1px solid ${tokens.border.default}`,
                  borderRadius: tokens.radius.sm,
                  px: 1.5,
                  py: 0.625,
                  whiteSpace: "nowrap",
                  "&:hover": { bgcolor: tokens.bg.hover, borderColor: tokens.border.default },
                }}
              >
                {isAdding ? "Adding..." : "Add"}
              </Button>
            </Stack>
          </Box>
        </Collapse>
      </Box>
    </Dialog>
  );
};

const CaseDetail: React.FC = () => {
  const { caseId } = useParams<{ caseId: string }>();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState(0);
  const [snackOpen, setSnackOpen] = useState(false);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [editCaseOpen, setEditCaseOpen] = useState(false);
  const [shareDialogOpen, setShareDialogOpen] = useState(false);

  const { user } = useAppSelector((state) => state.auth);
  const { selectedTenant } = useAppSelector((state) => state.tenantManagement);
  const isTDAdmin = user?.userRole === roles.TDADMIN;

  const tenantVars = useMemo(() => ({
    ...(isTDAdmin && selectedTenant && { tenantId: selectedTenant._id }),
  }), [isTDAdmin, selectedTenant]);

  const { data: fsCase, isLoading: caseLoading } = useGetFirstSaleCaseQuery(
    { caseId: caseId!, ...tenantVars },
    { skip: !caseId }
  );

  const { data: supplier } = useGetFirstSaleSupplierQuery(
    { supplierId: fsCase?.supplierId, ...tenantVars },
    { skip: !fsCase?.supplierId }
  );

  const { data: documents = [] } = useGetFirstSaleDocumentsQuery(
    { caseId: caseId!, ...tenantVars },
    { skip: !caseId }
  );

  const { data: validation } = useGetFirstSaleValidationQuery(
    { caseId: caseId!, ...tenantVars },
    { skip: !caseId }
  );

  const { data: activityEvents = [] } = useGetFirstSaleActivityEventsQuery(
    { caseId: caseId!, ...tenantVars },
    { skip: !caseId }
  );

  const { data: caseQuestions = [], isLoading: questionsLoading } = useGetFirstSaleCaseQuestionsQuery(
    { caseId: caseId!, ...tenantVars },
    { skip: !caseId }
  );

  const { data: generatedDocs = [] } = useGetFirstSaleGeneratedDocsQuery(
    { caseId: caseId!, ...tenantVars },
    { skip: !caseId }
  );

  const [generateToken] = useGenerateFirstSalePortalTokenMutation();
  const [deleteCase, { isLoading: isDeleting }] = useDeleteFirstSaleCaseMutation();

  const handleDeleteCase = async () => {
    if (!caseId) return;
    try {
      await deleteCase({ caseId, ...tenantVars }).unwrap();
      setDeleteConfirmOpen(false);
      navigate(fsCase?.supplierId ? `/first-sale/supplier/${fsCase.supplierId}` : "/first-sale");
    } catch (err) {
      console.error("Failed to delete case:", err);
    }
  };

  const handlePreviewPortal = () => {
    if (!caseId) return;
    navigate(`/first-sale/case/${caseId}/supplier-portal`);
  };

  const handleShareLink = async () => {
    if (!caseId) return;
    try {
      // Ensure a token exists before opening the dialog
      if (!fsCase?.portalToken) {
        await generateToken({ caseId, ...tenantVars }).unwrap();
      }
      setShareDialogOpen(true);
    } catch (err) {
      console.error("Failed to generate portal link:", err);
    }
  };

  const portalUrl = fsCase?.portalToken
    ? `${window.location.origin}/supplier-portal/${fsCase.portalToken}`
    : "";

  if (caseLoading) {
    return (
      <Box sx={{ p: 3 }}>
        <Skeleton width={300} height={24} />
        <Skeleton width={500} height={40} sx={{ mt: 2 }} />
      </Box>
    );
  }

  if (!fsCase) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography sx={{ color: tokens.text.secondary }}>Case not found.</Typography>
      </Box>
    );
  }

  const breadcrumbItems = supplier
    ? [
        { label: supplier.supplierName, href: `/first-sale/supplier/${supplier._id}` },
        { label: fsCase.caseNumber },
      ]
    : [{ label: fsCase.caseNumber }];

  const computedSavings =
    fsCase.middlemanValue != null && fsCase.factoryValue != null && fsCase.dutyRate != null && fsCase.dutyRate > 0
      ? (fsCase.middlemanValue - fsCase.factoryValue) * (fsCase.dutyRate / 100)
      : null;

  const kpis = [
    { label: "Middleman Value", value: formatCurrency(fsCase.middlemanValue) },
    { label: "Factory Value", value: formatCurrency(fsCase.factoryValue) },
    { label: "Duty Rate", value: fsCase.dutyRate != null ? `${fsCase.dutyRate}%` : "\u2014" },
    {
      label: "Savings",
      value: formatCurrency(computedSavings),
      color: computedSavings != null && computedSavings > 0 ? tokens.status.success : undefined,
    },
    { label: "Documents", value: documents.length.toString() },
  ];

  return (
    <Stack sx={{ height: "100%", overflow: "auto" }}>
      <Box sx={{ p: 3 }}>
        <FirstSaleBreadcrumb items={breadcrumbItems} />

        {/* Header */}
        <Stack direction="row" justifyContent="space-between" alignItems="flex-start" sx={{ mb: 2 }}>
          <Box>
            <Stack direction="row" alignItems="center" spacing={1.5} sx={{ mb: 0.5 }}>
              <Typography
                sx={{
                  fontSize: "1.1rem",
                  fontWeight: tokens.fontWeight.heavy,
                  color: tokens.text.primary,
                }}
              >
                {fsCase.caseNumber}
              </Typography>
              <StatusBadge status={fsCase.status} />
            </Stack>
            <Typography
              sx={{
                fontSize: tokens.fontSize.sm2,
                color: tokens.text.muted,
              }}
            >
              PO: {fsCase.poNumber}
              {fsCase.middlemanName && ` \u00B7 Middleman: ${fsCase.middlemanName}`}
              {fsCase.factoryName && ` \u00B7 Factory: ${fsCase.factoryName}`}
              {fsCase.htsCode && ` \u00B7 HTS: ${fsCase.htsCode}`}
            </Typography>
          </Box>
          <Stack direction="row" spacing={1} alignItems="center">
            <Button
              size="small"
              startIcon={<PreviewIcon sx={{ fontSize: 14 }} />}
              onClick={handlePreviewPortal}
              sx={{
                textTransform: "none",
                fontSize: tokens.fontSize.xs,
                fontWeight: tokens.fontWeight.semibold,
                color: tokens.text.primary,
                border: `1px solid ${tokens.border.default}`,
                borderRadius: tokens.radius.sm,
                px: 1.5,
                py: 0.5,
                "&:hover": { bgcolor: tokens.bg.hover },
              }}
            >
              Supplier Portal
            </Button>
            <Tooltip title="Share supplier portal">
              <IconButton
                size="small"
                onClick={handleShareLink}
                sx={{
                  border: `1px solid ${tokens.border.default}`,
                  borderRadius: tokens.radius.sm,
                  width: 32,
                  height: 32,
                  "&:hover": { bgcolor: tokens.bg.hover },
                }}
              >
                <ShareIcon sx={{ fontSize: 16 }} />
              </IconButton>
            </Tooltip>
            <Button
              size="small"
              startIcon={<EditIcon sx={{ fontSize: 14 }} />}
              onClick={() => setEditCaseOpen(true)}
              sx={{
                textTransform: "none",
                fontSize: tokens.fontSize.xs,
                fontWeight: tokens.fontWeight.semibold,
                color: tokens.text.primary,
                border: `1px solid ${tokens.border.default}`,
                borderRadius: tokens.radius.sm,
                px: 1.5,
                py: 0.5,
                "&:hover": { bgcolor: tokens.bg.hover },
              }}
            >
              Edit
            </Button>
            <Button
              size="small"
              startIcon={<DeleteIcon sx={{ fontSize: 14 }} />}
              onClick={() => setDeleteConfirmOpen(true)}
              sx={{
                textTransform: "none",
                fontSize: tokens.fontSize.xs,
                fontWeight: tokens.fontWeight.semibold,
                color: tokens.status.danger,
                border: `1px solid ${tokens.status.danger}`,
                borderRadius: tokens.radius.sm,
                px: 1.5,
                py: 0.5,
                "&:hover": { bgcolor: tokens.status.dangerBg },
              }}
            >
              Delete
            </Button>
          </Stack>
        </Stack>

        {/* KPI Row */}
        <Stack
          direction="row"
          spacing={0}
          sx={{
            mb: 3,
            border: `1px solid ${tokens.border.container}`,
            borderRadius: tokens.radius.md,
            overflow: "hidden",
          }}
        >
          {kpis.map((kpi, idx) => (
            <Box
              key={kpi.label}
              sx={{
                flex: 1,
                px: 2,
                py: 1.25,
                borderRight: idx < kpis.length - 1 ? `1px solid ${tokens.border.light}` : "none",
              }}
            >
              <Typography
                sx={{
                  fontSize: tokens.fontSize["2xs"],
                  textTransform: "uppercase",
                  letterSpacing: "0.05em",
                  color: tokens.text.muted,
                  lineHeight: "14px",
                  mb: 0.25,
                }}
              >
                {kpi.label}
              </Typography>
              <Typography
                sx={{
                  fontSize: tokens.fontSize.base,
                  fontWeight: tokens.fontWeight.bold,
                  color: kpi.color || tokens.text.primary,
                  fontVariantNumeric: "tabular-nums",
                }}
              >
                {kpi.value}
              </Typography>
            </Box>
          ))}
        </Stack>

        {/* Tabs */}
        <Tabs
          value={activeTab}
          onChange={(_, v) => setActiveTab(v)}
          sx={{
            borderBottom: `1px solid ${tokens.border.light}`,
            mb: 3,
            minHeight: 36,
            "& .MuiTabs-indicator": {
              backgroundColor: tokens.text.primary,
              height: 2,
            },
            "& .MuiTab-root": {
              textTransform: "none",
              fontSize: tokens.fontSize.sm2,
              fontWeight: tokens.fontWeight.semibold,
              color: tokens.text.secondary,
              minHeight: 36,
              px: 2,
              "&.Mui-selected": {
                color: tokens.text.primary,
              },
            },
          }}
        >
          <Tab label={`Documents & Validation (${documents.length})`} />
          <Tab label="Questionnaire" />
          <Tab label={`Generated Docs${generatedDocs.length > 0 ? ` (${generatedDocs.length})` : ""}`} />
          <Tab label="Activity" />
        </Tabs>

        {/* Tab Content */}
        {activeTab === 0 && (
          <DocumentList
            caseId={caseId!}
            supplierId={fsCase.supplierId}
            documents={documents}
            tenantId={tenantVars.tenantId}
            validation={validation}
            multipleMiddlemen={fsCase.multipleMiddlemen}
            backToBackFOB={fsCase.backToBackFOB}
            materialsProvided={fsCase.materialsProvided}
            relatedParties={fsCase.relatedParties}
            factorySellsToUnrelated={fsCase.factorySellsToUnrelated}
            middlemanBuysFromUnrelated={fsCase.middlemanBuysFromUnrelated}
            middlemanValue={fsCase.middlemanValue}
            dutyRate={fsCase.dutyRate}
            htsCode={fsCase.htsCode}
          />
        )}
        {activeTab === 1 && (
          <SupplierQuestionnaireTab
            questions={caseQuestions}
            isLoading={questionsLoading}
          />
        )}
        {activeTab === 2 && (
          <GeneratedDocsTab
            caseId={caseId!}
            caseNumber={fsCase?.caseNumber}
            caseData={fsCase}
            supplier={supplier}
            documents={documents}
            validation={validation}
            tenantId={tenantVars.tenantId}
          />
        )}
        {activeTab === 3 && (
          <ActivityTimeline events={activityEvents} />
        )}
      </Box>

      <EditCaseModal
        open={editCaseOpen}
        onClose={() => setEditCaseOpen(false)}
        caseData={fsCase}
        tenantId={tenantVars.tenantId}
      />

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteConfirmOpen} onClose={() => setDeleteConfirmOpen(false)}>
        <DialogTitle>Delete Case</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete case {fsCase.caseNumber}? This will permanently remove
            all documents, validation results, and activity history.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteConfirmOpen(false)} disabled={isDeleting}>
            Cancel
          </Button>
          <Button
            onClick={handleDeleteCase}
            disabled={isDeleting}
            sx={{
              color: tokens.status.danger,
              "&:hover": { bgcolor: tokens.status.dangerBg },
            }}
          >
            {isDeleting ? "Deleting..." : "Delete"}
          </Button>
        </DialogActions>
      </Dialog>

      <SharePortalDialog
        open={shareDialogOpen}
        onClose={() => setShareDialogOpen(false)}
        caseId={caseId!}
        portalUrl={portalUrl}
        portal2faEnabled={fsCase.portal2faEnabled ?? false}
        portalAuthorizedEmails={fsCase.portalAuthorizedEmails ?? []}
        tenantVars={tenantVars}
      />

      <Snackbar
        open={snackOpen}
        autoHideDuration={3000}
        onClose={() => setSnackOpen(false)}
        message="Portal link copied to clipboard"
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      />
    </Stack>
  );
};

export default CaseDetail;
